import { useState, Fragment } from "react";
import { BadgePercent, ChevronDown, ChevronRight, Coins, PhoneOff } from "lucide-react";
import { StatCard } from "@/components/StatCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTokenSavings } from "@/hooks/useTokenSavings";
import {
  formatCompactTokens,
  TOKEN_SAVINGS_SURFACE_LABELS,
  type TokenSavingsLever,
} from "@shared/tokenSavings";
import type { TokenSavingsRange } from "@/services/tokenSavingsService";

const RANGE_OPTIONS: Array<{ id: TokenSavingsRange; label: string }> = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "all", label: "All time" },
];

const LEVER_LABELS: Record<TokenSavingsLever, string> = {
  drawer: "Answer drawer",
  crisis_stop: "Crisis stop",
  private_hold: "Private hold",
  rag_pack: "RAG pack",
  history_trim: "History trim",
  librarian_pack: "Librarian pack",
  doc_clip: "Document clip",
  none: "Other",
};

function formatPercent(value: number | null): string {
  if (value == null) return "insufficient data";
  return `${Math.round(value * 100)}%`;
}

export default function TokenSavings() {
  const [range, setRange] = useState<TokenSavingsRange>("all");
  const [openSurface, setOpenSurface] = useState<string | null>("chat");
  const { data, isLoading } = useTokenSavings(range);
  const summary = data;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2" data-testid="text-token-savings-title">
          Token savings
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Each section is counted on its own. Percentages appear only when a baseline was measured.
          Older untagged calls are not backfilled.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {RANGE_OPTIONS.map((option) => (
          <Button
            key={option.id}
            size="sm"
            variant={range === option.id ? "default" : "outline"}
            onClick={() => setRange(option.id)}
            data-testid={`token-savings-range-${option.id}`}
          >
            {option.label}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Tokens saved"
          value={formatCompactTokens(summary?.totalSaved ?? 0)}
          icon={BadgePercent}
          subtitle={isLoading ? "Loading…" : "Measured avoided tokens"}
        />
        <StatCard
          title="Tokens used"
          value={formatCompactTokens(summary?.totalUsed ?? 0)}
          icon={Coins}
          subtitle="OpenRouter usage in this range"
        />
        <StatCard
          title="Calls avoided"
          value={summary?.callsAvoided ?? 0}
          icon={PhoneOff}
          subtitle="Drawer, crisis, or private hold"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By section</CardTitle>
          <CardDescription>
            Chat, MCP retrieval, and every other AI surface keep separate totals.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!summary?.bySurface.length ? (
            <p className="text-sm text-muted-foreground" data-testid="token-savings-empty">
              No measured savings or tagged usage yet. Send a chat, pack RAG, or ask the Librarian
              to start the ledger.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Section</TableHead>
                  <TableHead className="text-right">Saved</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Avoided calls</TableHead>
                  <TableHead className="text-right">Saved %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.bySurface.map((row) => {
                  const open = openSurface === row.surface;
                  const expandable = row.levers.length > 0;
                    return (
                    <Fragment key={row.surface}>
                      <TableRow
                        key={row.surface}
                        className={expandable ? "cursor-pointer" : undefined}
                        onClick={() => expandable && setOpenSurface(open ? null : row.surface)}
                        data-testid={`token-savings-row-${row.surface}`}
                      >
                        <TableCell className="font-medium">
                          <span className="inline-flex items-center gap-1">
                            {expandable ? (
                              open ? (
                                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                              )
                            ) : null}
                            {TOKEN_SAVINGS_SURFACE_LABELS[row.surface]}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{formatCompactTokens(row.tokensSaved)}</TableCell>
                        <TableCell className="text-right">{formatCompactTokens(row.tokensUsed)}</TableCell>
                        <TableCell className="text-right">{row.callsAvoided}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatPercent(row.percent)}
                        </TableCell>
                      </TableRow>
                      {open &&
                        row.levers.map((lever) => (
                          <TableRow key={`${row.surface}-${lever.lever}`} className="bg-muted/30">
                            <TableCell className="pl-10 text-muted-foreground">
                              {LEVER_LABELS[lever.lever] ?? lever.lever}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCompactTokens(lever.tokensSaved)}
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell className="text-right text-muted-foreground">
                              {lever.eventCount} events
                            </TableCell>
                          </TableRow>
                        ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
